import { z } from "zod/v4";
import { absentable, levelSchema, moneySchema, vatRateSchema } from "./catalog";
import { instantSchema, localDateSchema, registrationIdSchema, userIdSchema } from "./ids";

export const registrationStateSchema = z.enum(["confirmed", "waitlisted", "cancelled"]);
export type RegistrationState = z.infer<typeof registrationStateSchema>;

export const settlementKindSchema = z.enum(["unpaid", "paid", "waived"]);
export type SettlementKind = z.infer<typeof settlementKindSchema>;

export const settlementSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("unpaid") }),
	z.object({ at: instantSchema, byUserId: absentable(userIdSchema), kind: z.literal("paid") }),
	z.object({ at: instantSchema, byUserId: absentable(userIdSchema), kind: z.literal("waived") }),
]);
export type Settlement = z.infer<typeof settlementSchema>;

export const cancelledBySchema = z.enum(["participant", "organizer", "system"]);
export type CancelledBy = z.infer<typeof cancelledBySchema>;

/** Erasure removes the personal fields but keeps the seat, so each one may be absent. */
export const participantSchema = z.object({
	birthDate: absentable(localDateSchema),
	firstName: absentable(z.string()),
	id: z.uuid(),
	lastName: absentable(z.string()),
	level: absentable(levelSchema),
	notes: absentable(z.string()),
	shirtSize: absentable(z.string()),
});
export type Participant = z.infer<typeof participantSchema>;

export const contactSchema = z.object({
	email: absentable(z.string()),
	phone: absentable(z.string()),
});
export const guardianSchema = z.object({ name: z.string(), phone: absentable(z.string()) });

export const registrationSourceSchema = z.enum(["participant", "staff"]);

/** One registration as the roster shows it: the booker, the people, the money. `erased` rows keep seat, state and money only. */
export const rosterRegistrationSchema = z.object({
	cancelled: absentable(
		z.object({ at: instantSchema, by: cancelledBySchema, reason: absentable(z.string()) }),
	),
	contact: contactSchema,
	createdAt: instantSchema,
	erased: z.boolean().default(false),
	guardian: absentable(guardianSchema),
	id: registrationIdSchema,
	participants: z.array(participantSchema),
	settlement: settlementSchema,
	source: registrationSourceSchema,
	state: registrationStateSchema,
	total: moneySchema,
	vatRate: vatRateSchema,
	waitlistPosition: absentable(z.number().int().positive()),
});
export type RosterRegistration = z.infer<typeof rosterRegistrationSchema>;

const MAX_PARTICIPANTS = 8;
const trimmed = z.string().trim().min(1).max(120);

export const participantInputSchema = z.object({
	birthDate: localDateSchema,
	firstName: trimmed,
	lastName: trimmed,
	level: levelSchema.optional(),
	notes: z.string().trim().max(500).optional(),
	shirtSize: z.string().trim().max(10).optional(),
});

/** Staff add a registration on someone's behalf: the API refuses a `clientRequestId` here. */
export const staffRegistrationSchema = z.object({
	acceptedTerms: z.literal(true),
	contact: z.object({
		email: z.string().trim().pipe(z.email()),
		phone: z.string().trim().min(3).max(40),
	}),
	guardian: z.object({ name: trimmed, phone: z.string().trim().min(3).max(40) }).optional(),
	participants: z.array(participantInputSchema).min(1).max(MAX_PARTICIPANTS),
});
export type StaffRegistration = z.infer<typeof staffRegistrationSchema>;

export const MAX_PARTY_SIZE = MAX_PARTICIPANTS;
